import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Container, Row, Col, Button, Label, Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { JhiItemCount, JhiPagination, getSortState } from 'react-jhipster';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import TruncatedText from 'app/shared/common-fun/TruncatedText';
import { renderPreview, baseProfileImageUrl } from 'app/shared/common-fun';

interface Props {
  apiUrl: string;
  title: string;
  // the form to add a new post of this module; onDone is called after it was saved
  renderForm: (form: { onDone: () => void }) => React.ReactNode;
}

/**
 * Admin view of user submissions (Housing, Good Governance). Submissions are published
 * as soon as they are saved, so there is nothing to approve; the admin can add a new post or remove one.
 */
const ApprovalAdmin: React.FC<Props> = ({ apiUrl, title, renderForm }) => {
  const baseImageUrlProfile = baseProfileImageUrl;
  const [items, setItems] = useState<any[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(location, 20, 'id', 'desc'), location.search)
  );

  const fetchItems = async () => {
    setLoading(true);
    try {
      const result = await axios.get(
        `/${apiUrl}?page=${paginationState.activePage - 1}&size=${paginationState.itemsPerPage}&sort=${paginationState.sort},${
          paginationState.order
        }`
      );
      setItems(Array.isArray(result.data) ? result.data : []);
      setTotalItems(parseInt(result.headers['x-total-count'], 10) || 0);
    } catch (e) {
      setItems([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [paginationState, apiUrl]);

  const onRemove = async (id: number) => {
    if (!window.confirm(`Remove this ${title} submission?`)) {
      return;
    }
    try {
      await axios.delete(`/${apiUrl}/${id}`);
      await fetchItems();
    } catch (error) {
      console.error(`Error removing ${title}:`, error);
    }
  };

  const handleSaved = async () => {
    await fetchItems();
    setShowForm(false);
  };

  const handlePagination = currentPage => setPaginationState({ ...paginationState, activePage: currentPage });

  const formatDate = (dateString: string) => {
    const inputDate = new Date(dateString);
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    if (inputDate >= startOfToday) {
      return 'Today ' + inputDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
    }
    return inputDate.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true });
  };

  return (
    <Container className="inspiration-page card meeting-border">
      {showForm && (
        <>
          <Button color="link" className="px-0 mb-2" onClick={() => setShowForm(false)} data-cy="closeFormButton">
            <FontAwesomeIcon icon="arrow-left" /> Back to {title}
          </Button>
          {renderForm({ onDone: () => void handleSaved() })}
        </>
      )}
      <Row className="page-header">
        <Col md="6" className="heading">
          <h1>{title}</h1>
        </Col>
        <Col md="6" className="add-btn">
          {!showForm && (
            <div className="inspiration-add-btn d-flex align-items-center justify-content-center gap-2">
              <FontAwesomeIcon icon="plus" />
              <Label
                className="add-new-btn text-nowrap fs-6"
                data-cy="addPostButton"
                onClick={() => {
                  setShowForm(true);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Add new {title}
              </Label>
            </div>
          )}
        </Col>
      </Row>
      {
        <>
          <Row className={`inspiration-list loader-area ${loading && hasLoaded ? 'is-refreshing' : ''}`}>
            {!hasLoaded ? (
              <Spinner className="body-loader-icon" />
            ) : items.length > 0 ? (
              items.map(item => (
                <Col md="4" key={item.id} className="d-flex">
                  <div className="inspiration-item approval-card">
                    <TruncatedText className="inspiration-text" text={item.description} />
                    <TruncatedText className="inspiration-topic" text={item.topics} lines={2} />
                    <Col md={12} className="inspiration-img">
                      {renderPreview(item?.imageName)}
                    </Col>
                    <div className="inspiration-link">
                      <a href={`${item.link}`}>{item.link}</a>
                    </div>
                    <div className="inspiration-time">{formatDate(item.createdDate)}</div>
                    <div className="approval-card-footer approval-admin-footer">
                      <div className="inspiration-profile-details">
                        <div className="inspiration-user-profile">
                          {item?.user?.imageUrl ? (
                            <img className="img-thumbnail p-0" src={`${baseImageUrlProfile}/${item.user.imageUrl}`} />
                          ) : null}
                          <div className="inspiration-user-name">{`${item?.user?.firstName ?? ''} ${item?.user?.lastName ?? ''}`}</div>
                        </div>
                      </div>
                      <Button className="news-dlt-btn" onClick={() => void onRemove(item.id)}>
                        <FontAwesomeIcon icon="trash" />
                      </Button>
                    </div>
                  </div>
                </Col>
              ))
            ) : (
              <div className="connection-tab-gif">
                <Row>
                  <div className="col-md-6">
                    <p>No {title} submissions found.</p>
                  </div>
                  <div className="col-md-6">
                    <img src="content/images/inspiration_no_content.png" />
                  </div>
                </Row>
              </div>
            )}
          </Row>
          {totalItems > 0 && (
            <div className={items.length > 0 ? '' : 'd-none'}>
              <div className="justify-content-center d-flex">
                <JhiItemCount
                  page={paginationState.activePage}
                  total={totalItems}
                  itemsPerPage={paginationState.itemsPerPage}
                  i18nEnabled
                />
              </div>
              <div className="justify-content-center d-flex">
                <JhiPagination
                  activePage={paginationState.activePage}
                  onSelect={handlePagination}
                  maxButtons={5}
                  itemsPerPage={paginationState.itemsPerPage}
                  totalItems={totalItems}
                />
              </div>
            </div>
          )}
        </>
      }
    </Container>
  );
};

export default ApprovalAdmin;
